import { all, call, delay, fork, getContext, put, race, select, take, takeEvery, takeLatest } from 'redux-saga/effects'
import { SagaIterator } from 'redux-saga'
import { push } from 'redux-first-history'

import { logException } from '~/logging'
import { links, PATH } from '~/links'
import { uncurry } from '~/util/helpers/uncurry'
import { createAddressBookEntry, updateAddressBookEntry } from '~/address-book/model/AddressService'

import { get } from '~/util/api/get'

import {
  ACTION as SESSION_ACTION,
  type AddShipmentAction,
  type ConfirmStepAction,
  orderCompleted,
  orderFailed,
  orderSubmitted,
  type AddPromocodeAction,
  updatePackage,
  type UpdatePackageAction,
  type GetMarketingPreferenceAction,
  saveMarketingPreferenceFailure,
  saveMarketingPreferenceSuccess,
  updatePromocode,
  updateReceiver,
  type UpdateReceiverAction,
  type UpdateShipperAction,
  prefillBasketSessionAfterFailedPayment,
} from './session/actions'

import {
  ACTION as CAPABILITIES_ACTION,
  prefillCapabilitiesAfterFailedPaymentAction,
  updateCapabilitiesFailure,
  updateCapabilitiesRequested,
  type UpdateCapabilitiesRequestedAction,
  updateCapabilitiesSuccess,
} from './capabilities/actions'

import { ACTION as BASKET_ACTION, loadDefaultShipper } from '~/basket/model/actions'
import { ACTION as PROFILE_ACTION } from '~/profile/model/actions'

import { Basket, BasketShipment, BasketState } from './reducers'
import { fetchCapabilities } from './capabilities/service'
import { createOrder } from './order/createOrder'
import { doPayment, handlePaymentAction } from './order/doPayment'
import { basketSessionStorageName, OVERVIEW, PACKAGE, STEP_NAME } from './constants'
import { fetchPromocode } from './promocode/service'
import { MarketingPreference, PromocodeStatus } from './session/reducers'
import { getConfig } from '~/config'
import { handleLoadDefaultShipper, handleUpdateProfileSuccess } from '~/basket-integrations/profile'
import { PaymentProviderResult } from './order/types'
import { selectIsLoggedIn } from '../../account/model/selectors'

const { basePath } = getConfig()

/** ****************************************************************************
 * Helper sagas tasks
 **************************************************************************** */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function* handleUpdateCapabilitiesRequested(action: UpdateCapabilitiesRequestedAction): any {
  const { shipmentId, lookupCriteria } = action.payload
  try {
    const capabilities = yield call(
      fetchCapabilities,
      lookupCriteria.originCountry,
      lookupCriteria.destinationCountry,
      lookupCriteria.carrierKey,
      lookupCriteria.parcelTypeKey,
      lookupCriteria.toPostalCode,
      lookupCriteria.fromPostalCode,
      lookupCriteria.productKey,
      lookupCriteria.city
    )
    yield put(updateCapabilitiesSuccess(shipmentId, capabilities))
  } catch (error) {
    if (error instanceof Error) {
      yield put(updateCapabilitiesFailure(shipmentId, error))
    }
  }
}

/** ****************************************************************************
 * `handle` saga-function responding to package page actions
 **************************************************************************** */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function* handleUpdateCapabilities(action: UpdatePackageAction | AddShipmentAction): any {
  const selectors = yield getContext('selectors')
  const originCountry = yield select(selectors.getOriginCountry)
  const selectedShipment = yield select(selectors.getSelectedShipment)
  if (selectedShipment === null) {
    throw new Error('No selected shipment available')
  }

  const isLoading =
    (action.type === SESSION_ACTION.UPDATE_PACKAGE &&
      !action.payload.options.hasUpdatedParcelTypeKey &&
      !action.payload.options.hasUpdatedCarrierKey) ||
    action.type === SESSION_ACTION.ADD_SHIPMENT
  const carrierKey =
    (action as UpdatePackageAction).payload.options !== undefined &&
    ((action as UpdatePackageAction).payload.options.hasUpdatedCarrierKey ||
      (action as UpdatePackageAction).payload.options.hasUpdatedParcelTypeKey)
      ? selectedShipment.package.carrierKey
      : null
  yield put(
    updateCapabilitiesRequested(
      selectedShipment.id,
      {
        originCountry,
        destinationCountry: selectedShipment.package.destinationCountry,
        carrierKey,
        parcelTypeKey: selectedShipment.package.parcelTypeKey,
      },
      isLoading
    )
  )
}

function* handleUpdatePackage(action: UpdatePackageAction): SagaIterator {
  if (
    action.payload.options.hasUpdatedDestinationCountry ||
    action.payload.options.hasUpdatedParcelTypeKey ||
    action.payload.options.hasUpdatedCarrierKey
  ) {
    yield* handleUpdateCapabilities(action)
  }
}

function* handleAddShipment(action: AddShipmentAction): SagaIterator {
  if (action.payload.weight) {
    // eslint-disable-next-line func-names
    yield fork(function* () {
      yield take(CAPABILITIES_ACTION.UPDATE_CAPABILITIES_SUCCESS)

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const selectors: any = yield getContext('selectors')
      const parcelTypeKey: string = yield select(uncurry(selectors.getParcelTypeKeyByWeight), action.payload.weight)

      yield put(updatePackage({ parcelTypeKey }, { hasUpdatedParcelTypeKey: true }))
    })
  }

  yield put(loadDefaultShipper())

  yield* handleUpdateCapabilities(action)
}

/** ****************************************************************************
 * `handle` saga-function responding to delivery page actions
 **************************************************************************** */

/**
 * `handleUpdateReceiverCity` updates the capabilities with the new city
 */

function* handleUpdateReceiverCity(action: UpdateReceiverAction): SagaIterator {
  if (action.payload.options.hasUpdatedCity) {
    const selectors = yield getContext('selectors')
    const basket: Basket = yield select(selectors.getBasket)

    yield put(
      updateCapabilitiesRequested(basket.selectedShipmentId, {
        originCountry: basket.originCountry,
        destinationCountry: basket.package.destinationCountry,
        carrierKey: basket.package.carrierKey,
        parcelTypeKey: basket.package.parcelTypeKey,
        productKey: basket.productKey,
        city: action.payload.receiver.address ? action.payload.receiver.address.city : undefined,
      })
    )
  }
}

/**
 * `handleUpdateReceiverPostalCode` keeps the street and city of the receiver up-to-date with the filled
 * in postalcode and updates the capabilities
 */

function* handleUpdateReceiverPostalCode(action: UpdateReceiverAction): SagaIterator {
  if (action.payload.options.hasUpdatedPostalCode) {
    const selectors = yield getContext('selectors')
    const basket: Basket = yield select(selectors.getBasket)

    // Update capabilites (delivery options and transit times) with postalcode
    yield put(
      updateCapabilitiesRequested(basket.selectedShipmentId, {
        originCountry: basket.originCountry,
        destinationCountry: basket.package.destinationCountry,
        carrierKey: basket.package.carrierKey,
        parcelTypeKey: basket.package.parcelTypeKey,
        productKey: basket.productKey ? basket.productKey : undefined,
        toPostalCode: action.payload.receiver.address ? action.payload.receiver.address.postalCode : undefined,
      })
    )
  }
}

function* handleSaveReceiverToAddressbook(action: ConfirmStepAction): SagaIterator {
  const selectors = yield getContext('selectors')
  const basket: Basket = yield select(selectors.getBasket)

  const addressBookEntry = {
    id: basket.receiver.usedAddressBookEntryId,
    tags: ['RECEIVER'],
    email: basket.receiver.email,
    phoneNumber: basket.receiver.phoneNumber,
    name: {
      firstName: basket.receiver.firstName,
      lastName: basket.receiver.lastName,
      companyName: basket.receiver.companyName,
      additionalName: null,
    },
    address: {
      street: basket.receiver.address.street,
      number: basket.receiver.address.houseNumber,
      addition: basket.receiver.address.houseNumberSuffix,
      zipCode: basket.receiver.address.postalCode,
      city: basket.receiver.address.city,
      countryCode: basket.package.destinationCountry,
      additionalInfo: null,
      additionalAddressLine: basket.receiver.address.additionalAddressLine,
    },
  }

  if (action.payload.stepName === STEP_NAME.RECEIVER && basket.receiver.saveToAddressBook) {
    try {
      // In case the user goes back and then completes the shipper or receiver form again,
      // we need to make sure we don't create the address twice.
      // It needs to be updated instead.
      if (basket.receiver.usedAddressBookEntryId) {
        // @ts-ignore
        yield call(updateAddressBookEntry, addressBookEntry)
      } else {
        // @ts-ignore
        const id = yield call(createAddressBookEntry, addressBookEntry)
        yield put(updateReceiver({ usedAddressBookEntryId: id }, {}))
      }
    } catch (error) {
      logException(error)
    }
  }
}

/**
 * `handleUpdateShipper` updates the capabilities to make sure that the price for the shipment is correct
 * once shipper postalcode is known.
 */

function* handleUpdateShipper(action: UpdateShipperAction): SagaIterator {
  if (action.payload.options.hasUpdatedPostalCode) {
    const selectors = yield getContext('selectors')
    const basket: Basket = yield select(selectors.getBasket)

    yield put(
      updateCapabilitiesRequested(basket.selectedShipmentId, {
        originCountry: basket.originCountry,
        destinationCountry: basket.package.destinationCountry,
        carrierKey: basket.package.carrierKey,
        parcelTypeKey: basket.package.parcelTypeKey,
        fromPostalCode: action.payload.shipper.address ? action.payload.shipper.address.postalCode : undefined,
        toPostalCode: basket.receiver.address.postalCode || undefined,
        city: basket.receiver.address.city || undefined,
        productKey: basket.productKey,
      })
    )
  }
}

/** ****************************************************************************
 * `handle` saga-function responding to payment actions
 **************************************************************************** */

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function* handleFetchMarketingPreference(action: GetMarketingPreferenceAction): SagaIterator {
  try {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const { wantsNewsAndPromotions } = yield call(fetchMarketingPreferences)
    yield put(saveMarketingPreferenceSuccess(wantsNewsAndPromotions))
  } catch {
    yield put(saveMarketingPreferenceFailure(false))
  }
}

function* handleAddPromocode(action: AddPromocodeAction): SagaIterator {
  const selectors = yield getContext('selectors')
  const originCountry = yield select(selectors.getOriginCountry)
  const { promocode, shipmentId, shipments } = action.payload
  try {
    const { percentage, carrier, originCountries, destinationCountries, maxUses } = yield call(
      fetchPromocode,
      promocode
    )

    if (
      (originCountries.length === 0 || originCountries.includes(originCountry)) &&
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      isPromcodeApplicable(shipmentId, promocode, maxUses, shipments)
    ) {
      yield put(
        updatePromocode(shipmentId, {
          status: PromocodeStatus.Valid,
          discount: {
            percentage,
            destinationCountries,
            carrier,
          },
          value: promocode,
          maxUses,
        })
      )
    } else {
      yield put(
        updatePromocode(shipmentId, {
          status: PromocodeStatus.Invalid,
          discount: null,
          value: promocode,
          maxUses,
        })
      )
    }
  } catch {
    yield put(
      updatePromocode(shipmentId, {
        status: PromocodeStatus.Invalid,
        discount: null,
        value: promocode,
        maxUses: 0,
      })
    )
  }
}

function* redirectToOverview(): SagaIterator {
  const selectors = yield getContext('selectors')
  const state = yield select()

  const { confirmedSteps } = selectors.getSelectedShipment(state)

  if (confirmedSteps.includes(STEP_NAME.SHIPPER)) {
    // Not sure this is the best way. I prefer to keep state & logic seperated from routing & rendering
    // Imagine, running all the sagas on the backend, then this would not work :)
    yield put(push(`${basePath}${PATH.BASKET}/${OVERVIEW}`))
  }
}

function* submitOrder(action: ConfirmStepAction) {
  // @ts-ignore
  const selectors = yield getContext('selectors')

  const basket: Basket = yield select(selectors.getBasket)
  const getBasketWithCapabilities: BasketState = yield select(selectors.getBasketWithCapabilities)
  const { stepName } = action.payload
  const { isParcelshopPaymentEnabled, isSpaServiceAvailable } = basket.config

  const isNormalPayment = stepName === STEP_NAME.OVERVIEW && !isParcelshopPaymentEnabled
  const isOtcPayment = stepName === STEP_NAME.PARCELSHOP_PAYMENT && isParcelshopPaymentEnabled

  if (!isNormalPayment && !isOtcPayment) {
    return
  }

  yield put(orderSubmitted())

  try {
    window?.sessionStorage?.setItem(basketSessionStorageName, JSON.stringify(getBasketWithCapabilities))

    // @ts-ignore
    const result = yield createOrder(basket)

    if (isNormalPayment) {
      const orderId = result.order.id
      const paymentProviderData: PaymentProviderResult = yield doPayment(
        orderId,
        basket.payment.usePaymentService,
        basket.payment.paymentType,
        basket.payment.issuerId
      )

      handlePaymentAction(paymentProviderData.action)
    } else {
      yield put(orderCompleted())

      if (isSpaServiceAvailable) {
        window.location.href = links.spaInventoryLink()
      } else {
        yield put(push(`${basePath}${PATH.DASHBOARD}`))
      }
    }
  } catch (err) {
    logException(err)
    yield put(orderFailed())
  }
}

const isPromcodeApplicable = (
  shipmentId: number,
  promocode: string,
  maxUses: number,
  shipments: BasketShipment[]
): boolean => {
  const occurrences = shipments
    .filter((shipment) => shipment.id !== shipmentId && shipment.promocode?.status === PromocodeStatus.Valid)
    .reduce((counter, shipment) => {
      if (shipment.promocode?.value === promocode) {
        // eslint-disable-next-line no-param-reassign
        counter += 1
      }
      return counter
    }, 1)
    .valueOf()
  return occurrences ? occurrences <= maxUses : true
}

const fetchMarketingPreferences = async (): Promise<MarketingPreference> => {
  const {
    features: { loyalty },
  } = getConfig()
  const result = await get<MarketingPreference>(`${loyalty.endpoint}/marketing-settings`)
  return result
}

export function* handleRestoreFromSessionState() {
  try {
    const { isLoggedIn } = yield select(selectIsLoggedIn)
    yield all([
      race({
        action1: take(PROFILE_ACTION.UPDATE_PROFILE_SUCCESS),
        action2: take(PROFILE_ACTION.UPDATE_PROFILE_FAILURE),
      }),
      take(CAPABILITIES_ACTION.UPDATE_CAPABILITIES_SUCCESS),
      ...(isLoggedIn ? [take(SESSION_ACTION.UPDATE_SHIPPER)] : []),
    ])

    const getBasketFromSession = sessionStorage?.getItem(basketSessionStorageName)

    if (!getBasketFromSession) {
      yield put(push(`${basePath}${PATH.BASKET}/${PACKAGE}`))
      return
    }

    // Sometimes a spinner stay visible, we do not want to spend more time to figure out
    // what we can do to solve this problem. This solution is temporary anyway.
    yield delay(350)

    const basketState: BasketState = JSON.parse(getBasketFromSession)
    const { session, capabilities } = basketState
    yield put(prefillCapabilitiesAfterFailedPaymentAction(capabilities))
    yield put(prefillBasketSessionAfterFailedPayment(session))
    sessionStorage?.removeItem(basketSessionStorageName)
    yield put(push(`${basePath}${PATH.BASKET}/${OVERVIEW}`))
  } catch {
    yield put(push(`${basePath}${PATH.BASKET}/${PACKAGE}`))
  }
}

export function* basketSaga() {
  yield all([
    takeLatest(SESSION_ACTION.REMOVE_SHIPMENT, redirectToOverview),
    takeLatest(SESSION_ACTION.ADD_SHIPMENT, handleAddShipment),
    takeEvery(SESSION_ACTION.UPDATE_PACKAGE, handleUpdatePackage),
    takeLatest(SESSION_ACTION.UPDATE_RECEIVER, handleUpdateReceiverPostalCode),
    takeLatest(SESSION_ACTION.UPDATE_RECEIVER, handleUpdateReceiverCity),
    takeLatest(SESSION_ACTION.CONFIRM_STEP, handleSaveReceiverToAddressbook),
    takeLatest(SESSION_ACTION.UPDATE_SHIPPER, handleUpdateShipper),
    takeLatest(SESSION_ACTION.ADD_PROMOCODE, handleAddPromocode),
    takeLatest(SESSION_ACTION.FETCH_MKT_PREFERENCE, handleFetchMarketingPreference),
    takeLatest(SESSION_ACTION.CONFIRM_STEP, submitOrder),
    takeLatest(BASKET_ACTION.LOAD_DEFAULT_SHIPPER, handleLoadDefaultShipper),
    takeLatest(PROFILE_ACTION.UPDATE_PROFILE_SUCCESS, handleUpdateProfileSuccess),
    takeLatest(CAPABILITIES_ACTION.UPDATE_CAPABILITIES_REQUESTED, handleUpdateCapabilitiesRequested),
    takeLatest(BASKET_ACTION.RESTORE_FROM_SESSION_STATE, handleRestoreFromSessionState),
  ])
}
